/**
 * De voorkeuren van de schil die op het hele scherm werken (ronde 3,
 * 28-10-2026): de maat van het venster, de dichtheid en de zijbalk.
 *
 * Alle drie komen als attribuut op `<html>` (`data-viewport`, `data-density`,
 * `data-sidebar`), zodat CSS ze zonder eigen `@media`-getallen kan lezen. De
 * waarden zelf staan in `styles/breakpoints.ts` en `styles/density.ts`.
 *
 * Een kleine eigen store met `useSyncExternalStore` in plaats van context: de
 * kop (gebruikersmenu) en de schil lezen dezelfde keuze, en een wijziging in een
 * ander tabblad (`storage`-event) komt zo ook binnen.
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { useAuth } from "@/auth/authState";
import { type Viewport, viewportFor } from "@/styles/breakpoints";
import { type Density, readDensity, resolveDensity, writeDensity } from "@/styles/density";

const luisteraars = new Set<() => void>();
const meld = () => luisteraars.forEach((luisteraar) => luisteraar());

function abonneer(luisteraar: () => void) {
  luisteraars.add(luisteraar);
  window.addEventListener("resize", luisteraar);
  window.addEventListener("storage", luisteraar);
  return () => {
    luisteraars.delete(luisteraar);
    window.removeEventListener("resize", luisteraar);
    window.removeEventListener("storage", luisteraar);
  };
}

const breedte = () => window.innerWidth;

/** De breedte van het venster in CSS-pixels, live. */
export function useViewportWidth(): number {
  return useSyncExternalStore(abonneer, breedte, () => 1600);
}

/** `wide`, `medium` of `narrow` (zie `styles/breakpoints.ts`). */
export function useViewport(): Viewport {
  return viewportFor(useViewportWidth());
}

/** Wie de gebruiker is, als sleutel voor de opgeslagen keuzes. */
function useUserKey(): string | null {
  const { account } = useAuth();
  return account?.email ?? account?.subject ?? null;
}

/** De dichtheid die geldt, en een functie om zelf te kiezen. */
export function useDensity(): [Density, (density: Density) => void] {
  const gebruiker = useUserKey();
  const breed = useViewportWidth();
  const gekozen = useSyncExternalStore(abonneer, () => readDensity(gebruiker), () => null);
  const kies = useCallback(
    (density: Density) => {
      writeDensity(gebruiker, density);
      meld();
    },
    [gebruiker],
  );
  return [resolveDensity(gekozen, breed), kies];
}

const ZIJBALK_SLEUTEL = "atlas.sidebar";

function leesZijbalk(): "dicht" | "open" | null {
  try {
    const waarde = window.localStorage.getItem(ZIJBALK_SLEUTEL);
    return waarde === "dicht" || waarde === "open" ? waarde : null;
  } catch {
    return null;
  }
}

/**
 * Of de zijbalk ingeklapt is (alleen iconen). Zonder eigen keuze is hij dicht
 * bij `narrow` en open daarboven; een keuze wordt onthouden.
 */
export function useSidebarCollapsed(): [boolean, (collapsed: boolean) => void] {
  const maat = useViewport();
  const gekozen = useSyncExternalStore(abonneer, leesZijbalk, () => null);
  const kies = useCallback((collapsed: boolean) => {
    try {
      window.localStorage.setItem(ZIJBALK_SLEUTEL, collapsed ? "dicht" : "open");
    } catch {
      // Geen opslag: de keuze geldt tot het venster van maat verandert.
    }
    meld();
  }, []);
  const ingeklapt = gekozen === null ? maat === "narrow" : gekozen === "dicht";
  return [ingeklapt, kies];
}

/** Zet een attribuut op `<html>` zolang het onderdeel bestaat. */
function useRootAttribute(naam: string, waarde: string) {
  useEffect(() => {
    document.documentElement.setAttribute(naam, waarde);
  }, [naam, waarde]);
}

/**
 * In de schil: zet `data-viewport`, `data-density` en `data-sidebar` op
 * `<html>`. `main.tsx` zet `data-viewport` al vóór de eerste weergave, zodat er
 * niets verspringt.
 */
export function useShellAttributes(): { collapsed: boolean; setCollapsed: (collapsed: boolean) => void } {
  const maat = useViewport();
  const [dichtheid] = useDensity();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  useRootAttribute("data-viewport", maat);
  useRootAttribute("data-density", dichtheid);
  useRootAttribute("data-sidebar", collapsed ? "collapsed" : "expanded");
  return { collapsed, setCollapsed };
}
